import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getJSON, patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { CustomField, CustomFieldsResponse } from '@/lib/contacts/types'
import { CustomFieldDialogs } from '@/pages/CustomFieldDialogs'
import { CustomFieldsList } from '@/pages/CustomFieldsList'
import { useCustomFieldDialogState } from '@/pages/useCustomFieldDialogState'
import { useCustomFieldLifecycle } from '@/pages/useCustomFieldLifecycle'

export function CustomFieldsPage() {
  const queryClient = useQueryClient()
  const dialogs = useCustomFieldDialogState()
  const lifecycle = useCustomFieldLifecycle()
  const customFieldsQuery = useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => getJSON<CustomFieldsResponse>('/api/v1/custom_fields'),
    retry: false,
  })
  const reorderCustomFields = useMutation({
    mutationFn: (customFields: CustomField[]) =>
      patchJSON<CustomFieldsResponse>('/api/v1/custom_fields/reorder', {
        custom_field_ids: customFields.map((field) => field.id),
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(['custom-fields'], response)
    },
  })

  return (
    <>
      <CustomFieldsList
        query={customFieldsQuery}
        lifecycleError={lifecycle.error}
        reorderError={customFieldOrderErrorMessage(reorderCustomFields.error)}
        reorderPending={reorderCustomFields.isPending}
        onCreate={dialogs.openCreate}
        onEdit={dialogs.openEdit}
        onReorder={(customFields) => reorderCustomFields.mutate(customFields)}
        onSetActive={lifecycle.setActiveState}
        onDelete={lifecycle.deleteField}
      />

      <CustomFieldDialogs {...dialogs.dialogProps} />
    </>
  )
}

function customFieldOrderErrorMessage(error: Error | null) {
  if (!error) return null

  return apiFieldMessages(error, 'custom_field_order')[0] ?? error.message
}
